import type React from 'react';
import { Check } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

interface CheckboxProps {
  label?: React.ReactNode;
  checked: boolean;
  className?: string;
  labelClassName?: string;
  id?: string;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  ariaLabel?: string;
  /** aria-controls: id of the region this checkbox shows/hides; omit while that region is unmounted. */
  ariaControls?: string;
  /** aria-expanded: whether the region named by ariaControls is currently shown. */
  ariaExpanded?: boolean;
  /** aria-describedby: id of the element holding a hint for this checkbox. */
  ariaDescribedBy?: string;
  ref?: React.Ref<HTMLInputElement>;
}

const CHECKBOX_CLASSES =
  'peer size-5 cursor-pointer appearance-none rounded-sm border border-mp-input bg-mp-surface transition-colors motion-reduce:transition-none checked:border-mp-primary checked:bg-mp-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mp-link disabled:cursor-not-allowed forced-colors:appearance-auto';
const CHECK_MARK_CLASSES =
  'pointer-events-none absolute inset-0.5 hidden size-4 text-mp-surface peer-checked:block forced-colors:hidden';

export const Checkbox: React.FC<CheckboxProps> = ({
  label,
  checked,
  id,
  onChange,
  className,
  labelClassName = 'text-sm',
  disabled = false,
  ariaLabel,
  ariaControls,
  ariaExpanded,
  ariaDescribedBy,
  ref
}) => {
  return (
    <label
      htmlFor={id}
      className={`inline-flex items-center gap-3 group ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
        }`}
    >
      <span className="relative inline-flex size-5 shrink-0">
        <input
          ref={ref}
          id={id}
          type="checkbox"
          className={twMerge(CHECKBOX_CLASSES, className)}
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          disabled={disabled}
          aria-label={ariaLabel}
          aria-controls={ariaControls}
          aria-expanded={ariaExpanded}
          aria-describedby={ariaDescribedBy}
        />
        <Check aria-hidden="true" strokeWidth={3} className={CHECK_MARK_CLASSES} />
      </span>
      {label && <span className={labelClassName}>{label}</span>}
    </label>
  );
};
